import type { DocPageContent } from '../types';

/**
 * Per-framework tutorial factory (launch-readiness B9 — AEO content).
 *
 * One shared block template instantiated per framework, so the i18n surface
 * stays bounded: shared prose keys + three per-framework keys (title,
 * description, what-the-extractor-reads). The route snippets are VERBATIM
 * code (never translated) and each one exercises exactly the detection
 * signals documented in `docs/architecture/framework-support.md` — the
 * snippet shown is a snippet the extractor genuinely composes, full paths
 * included. No aspirational claims: every framework here has full-path
 * composition per that table.
 */

export interface FrameworkGuide {
  readonly id: string;
  /** Verbatim framework name (identical across locales). */
  readonly name: string;
  /** Code-fence language for the snippet. */
  readonly snippetLanguage: string;
  /** Representative routes the extractor reads — matches framework-support.md. */
  readonly snippet: string;
}

export const FRAMEWORK_GUIDES: readonly FrameworkGuide[] = [
  {
    id: 'express',
    name: 'Express',
    snippetLanguage: 'ts',
    snippet: `// app.ts
app.use('/api', ordersRouter);

// orders.router.ts
const ordersRouter = Router();
ordersRouter.get('/orders/:id', getOrder);      // → GET /api/orders/:id
ordersRouter.post('/orders/:id/refund', refund); // → POST /api/orders/:id/refund`,
  },
  {
    id: 'nestjs',
    name: 'NestJS',
    snippetLanguage: 'ts',
    snippet: `// main.ts
app.setGlobalPrefix('api');

// orders.controller.ts
@Controller('orders')
export class OrdersController {
  @Get(':id')                       // → GET /api/orders/:id
  @Post(':id/refund')               // → POST /api/orders/:id/refund
}`,
  },
  {
    id: 'nextjs',
    name: 'Next.js',
    snippetLanguage: 'ts',
    snippet: `// app/api/orders/[id]/route.ts   → GET /api/orders/[id]
export async function GET(req: Request) { /* … */ }

// app/api/orders/[id]/refund/route.ts → POST /api/orders/[id]/refund
export async function POST(req: Request) { /* … */ }`,
  },
  {
    id: 'fastify',
    name: 'Fastify',
    snippetLanguage: 'ts',
    snippet: `// server.ts
await fastify.register(ordersPlugin, { prefix: '/api' });

// orders.plugin.ts
fastify.get('/orders/:id', getOrder);        // → GET /api/orders/:id
fastify.post('/orders/:id/refund', refund);  // → POST /api/orders/:id/refund`,
  },
  {
    id: 'fastapi',
    name: 'FastAPI',
    snippetLanguage: 'python',
    snippet: `# main.py
app.include_router(orders_router, prefix="/api")

# orders.py
orders_router = APIRouter(prefix="/orders")

@orders_router.get("/{order_id}")          # → GET /api/orders/{order_id}
@orders_router.post("/{order_id}/refund")  # → POST /api/orders/{order_id}/refund`,
  },
  {
    id: 'django',
    name: 'Django',
    snippetLanguage: 'python',
    snippet: `# project/urls.py
urlpatterns = [path("api/", include("orders.urls"))]

# orders/urls.py
urlpatterns = [
    path("orders/<int:pk>/", views.OrderDetail.as_view()),    # → GET /api/orders/<pk>/
    path("orders/<int:pk>/refund/", views.refund),            # → POST /api/orders/<pk>/refund/
]`,
  },
  {
    id: 'rails',
    name: 'Rails',
    snippetLanguage: 'ruby',
    snippet: `# config/routes.rb
namespace :api do
  resources :orders, only: [:index, :show] do
    member { post :refund }   # → POST /api/orders/:id/refund
  end
end`,
  },
  {
    id: 'laravel',
    name: 'Laravel',
    snippetLanguage: 'php',
    snippet: `// routes/api.php (the /api prefix comes from the route service provider)
Route::get('/orders/{order}', [OrderController::class, 'show']);
Route::post('/orders/{order}/refund', [OrderController::class, 'refund']);
// → GET /api/orders/{order} · POST /api/orders/{order}/refund`,
  },
] as const;

const INIT_OUTPUT = `$ npx syncanix init
✓ detected framework
✓ scanned routes
✓ wrote .syncanix/catalog.json
→ review your capabilities in the dashboard`;

export function frameworkGuidePage(guide: FrameworkGuide): DocPageContent {
  return {
    slug: `frameworks/${guide.id}`,
    titleKey: `docs.fwguide.${guide.id}.title`,
    descriptionKey: `docs.fwguide.${guide.id}.description`,
    blocks: [
      { kind: 'paragraph', textKey: `docs.fwguide.${guide.id}.lead` },
      { kind: 'heading', level: 2, id: 'routes', textKey: 'docs.fwguide.shared.routes.heading' },
      { kind: 'paragraph', textKey: 'docs.fwguide.shared.routes.body' },
      {
        kind: 'code',
        language: guide.snippetLanguage,
        code: guide.snippet,
        captionKey: 'docs.fwguide.shared.routes.caption',
      },
      { kind: 'paragraph', textKey: `docs.fwguide.${guide.id}.reads` },
      { kind: 'heading', level: 2, id: 'run', textKey: 'docs.fwguide.shared.run.heading' },
      { kind: 'paragraph', textKey: 'docs.fwguide.shared.run.body' },
      { kind: 'code', language: 'bash', code: INIT_OUTPUT },
      {
        kind: 'callout',
        tone: 'tip',
        titleKey: 'docs.fwguide.shared.override.title',
        textKey: 'docs.fwguide.shared.override.body',
      },
      { kind: 'heading', level: 2, id: 'review', textKey: 'docs.fwguide.shared.review.heading' },
      { kind: 'paragraph', textKey: 'docs.fwguide.shared.review.body' },
      { kind: 'heading', level: 2, id: 'ship', textKey: 'docs.fwguide.shared.ship.heading' },
      { kind: 'paragraph', textKey: 'docs.fwguide.shared.ship.body' },
      { kind: 'heading', level: 2, id: 'next', textKey: 'docs.common.nextSteps' },
      {
        kind: 'cardGrid',
        cards: [
          {
            titleKey: 'docs.fwguide.shared.related.frameworks.title',
            textKey: 'docs.fwguide.shared.related.frameworks.text',
            to: { docSlug: 'cli/frameworks' },
          },
          {
            titleKey: 'docs.fwguide.shared.related.launch.title',
            textKey: 'docs.fwguide.shared.related.launch.text',
            to: { docSlug: 'guides/launch-assistant' },
          },
        ],
      },
    ],
  };
}
